import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import { db } from '../firebase';

const NUMERIC_STAT_KEYS = [
  'invoicesTotal',
  'invoicesPaid',
  'invoicesOpen',
  'incomeTotal',
  'expensesTotal',
  'paymentsTotal',
  'invoicesCount',
  'incomeCount',
  'expensesCount',
  'paymentsCount',
];

const monthKeyFromDate = (value = new Date()) => {
  const d = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(d.getTime())) {
    const fallback = new Date();
    const year = fallback.getFullYear();
    const month = String(fallback.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const emptyStats = (monthKey) => ({
  monthKey,
  invoicesTotal: 0,
  invoicesPaid: 0,
  invoicesOpen: 0,
  incomeTotal: 0,
  expensesTotal: 0,
  paymentsTotal: 0,
  invoicesCount: 0,
  incomeCount: 0,
  expensesCount: 0,
  paymentsCount: 0,
  updatedAt: serverTimestamp(),
});

async function applyStatsDelta(monthKey, deltas = {}) {
  const ref = doc(db, 'stats', monthKey);

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    const current = snap.exists()
      ? { ...emptyStats(monthKey), ...snap.data() }
      : emptyStats(monthKey);

    const next = {
      monthKey,
      updatedAt: serverTimestamp(),
    };

    for (const key of NUMERIC_STAT_KEYS) {
      const baseValue = Number(current[key] || 0);
      const deltaValue = Number(deltas[key] || 0);
      const computedValue = baseValue + deltaValue;
      next[key] = Math.max(0, Number.isFinite(computedValue) ? computedValue : baseValue);
    }

    transaction.set(ref, next, { merge: true });
  });
}

export async function ensureMonthlyStats(monthKey = monthKeyFromDate()) {
  const ref = doc(db, 'stats', monthKey);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    await setDoc(ref, emptyStats(monthKey), { merge: true });
    return emptyStats(monthKey);
  }

  return { ...emptyStats(monthKey), ...snap.data() };
}

export async function getMonthlyStats(monthKey = monthKeyFromDate()) {
  const ref = doc(db, 'stats', monthKey);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    await setDoc(ref, emptyStats(monthKey), { merge: true });
    return emptyStats(monthKey);
  }

  return { ...emptyStats(monthKey), ...snap.data() };
}

export async function patchMonthlyStats(monthKey, patch = {}) {
  const ref = doc(db, 'stats', monthKey);
  await setDoc(
    ref,
    {
      monthKey,
      ...patch,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export function buildStatsPatch({
  prevInvoice = null,
  nextInvoice = null,
  prevIncome = null,
  nextIncome = null,
  prevExpense = null,
  nextExpense = null,
  prevPayment = null,
  nextPayment = null,
}) {
  const patch = {};

  const addNumber = (key, delta) => {
    patch[key] = Number((patch[key] || 0) + Number(delta || 0));
  };

  if (prevInvoice || nextInvoice) {
    const prevTotal = Number(prevInvoice?.grandTotal || prevInvoice?.total || 0);
    const nextTotal = Number(nextInvoice?.grandTotal || nextInvoice?.total || 0);
    const prevPaid = Number(prevInvoice?.paidAmount || 0);
    const nextPaid = Number(nextInvoice?.paidAmount || 0);

    addNumber('invoicesTotal', nextTotal - prevTotal);
    addNumber('invoicesPaid', nextPaid - prevPaid);
    addNumber('invoicesOpen', (nextTotal - nextPaid) - (prevTotal - prevPaid));

    const hadPrev = !!prevInvoice;
    const hasNext = !!nextInvoice;
    if (!hadPrev && hasNext) addNumber('invoicesCount', 1);
    if (hadPrev && !hasNext) addNumber('invoicesCount', -1);
  }

  if (prevIncome || nextIncome) {
    const prevAmount = Number(prevIncome?.amount || 0);
    const nextAmount = Number(nextIncome?.amount || 0);
    addNumber('incomeTotal', nextAmount - prevAmount);

    const hadPrev = !!prevIncome;
    const hasNext = !!nextIncome;
    if (!hadPrev && hasNext) addNumber('incomeCount', 1);
    if (hadPrev && !hasNext) addNumber('incomeCount', -1);
  }

  if (prevExpense || nextExpense) {
    const prevAmount = Number(prevExpense?.amount || 0);
    const nextAmount = Number(nextExpense?.amount || 0);
    addNumber('expensesTotal', nextAmount - prevAmount);

    const hadPrev = !!prevExpense;
    const hasNext = !!nextExpense;
    if (!hadPrev && hasNext) addNumber('expensesCount', 1);
    if (hadPrev && !hasNext) addNumber('expensesCount', -1);
  }

  if (prevPayment || nextPayment) {
    const prevAmount = Number(prevPayment?.amount || 0);
    const nextAmount = Number(nextPayment?.amount || 0);
    addNumber('paymentsTotal', nextAmount - prevAmount);

    const hadPrev = !!prevPayment;
    const hasNext = !!nextPayment;
    if (!hadPrev && hasNext) addNumber('paymentsCount', 1);
    if (hadPrev && !hasNext) addNumber('paymentsCount', -1);
  }

  return patch;
}

export async function addInvoiceToStats(invoice) {
  const monthKey = monthKeyFromDate(invoice?.invoiceDate || invoice?.date || new Date());
  const total = Number(invoice?.grandTotal || invoice?.total || 0);
  const paid = Number(invoice?.paidAmount || 0);

  await applyStatsDelta(monthKey, {
    invoicesTotal: total,
    invoicesPaid: paid,
    invoicesOpen: Math.max(0, total - paid),
    invoicesCount: 1,
  });
}

export async function updateInvoiceInStats(prevInvoice, nextInvoice) {
  const prevMonthKey = monthKeyFromDate(prevInvoice?.invoiceDate || prevInvoice?.date || new Date());
  const nextMonthKey = monthKeyFromDate(nextInvoice?.invoiceDate || nextInvoice?.date || new Date());

  if (prevMonthKey !== nextMonthKey) {
    await removeInvoiceFromStats(prevInvoice);
    await addInvoiceToStats(nextInvoice);
    return;
  }

  const prevTotal = Number(prevInvoice?.grandTotal || prevInvoice?.total || 0);
  const nextTotal = Number(nextInvoice?.grandTotal || nextInvoice?.total || 0);
  const prevPaid = Number(prevInvoice?.paidAmount || 0);
  const nextPaid = Number(nextInvoice?.paidAmount || 0);

  await applyStatsDelta(nextMonthKey, {
    invoicesTotal: nextTotal - prevTotal,
    invoicesPaid: nextPaid - prevPaid,
    invoicesOpen: Math.max(0, nextTotal - nextPaid) - Math.max(0, prevTotal - prevPaid),
  });
}

export async function removeInvoiceFromStats(invoice) {
  const monthKey = monthKeyFromDate(invoice?.invoiceDate || invoice?.date || new Date());
  const total = Number(invoice?.grandTotal || invoice?.total || 0);
  const paid = Number(invoice?.paidAmount || 0);

  await applyStatsDelta(monthKey, {
    invoicesTotal: -total,
    invoicesPaid: -paid,
    invoicesOpen: -Math.max(0, total - paid),
    invoicesCount: -1,
  });
}

export async function addIncomeToStats(entry) {
  const monthKey = monthKeyFromDate(entry?.date || entry?.createdAt || new Date());
  const amount = Number(entry?.amount || 0);

  await applyStatsDelta(monthKey, {
    incomeTotal: amount,
    incomeCount: 1,
  });
}

export async function updateIncomeInStats(prevEntry, nextEntry) {
  const prevMonthKey = monthKeyFromDate(prevEntry?.date || prevEntry?.createdAt || new Date());
  const nextMonthKey = monthKeyFromDate(nextEntry?.date || nextEntry?.createdAt || new Date());

  if (prevMonthKey !== nextMonthKey) {
    await removeIncomeFromStats(prevEntry);
    await addIncomeToStats(nextEntry);
    return;
  }

  const prevAmount = Number(prevEntry?.amount || 0);
  const nextAmount = Number(nextEntry?.amount || 0);

  await applyStatsDelta(nextMonthKey, {
    incomeTotal: nextAmount - prevAmount,
  });
}

export async function removeIncomeFromStats(entry) {
  const monthKey = monthKeyFromDate(entry?.date || entry?.createdAt || new Date());
  const amount = Number(entry?.amount || 0);

  await applyStatsDelta(monthKey, {
    incomeTotal: -amount,
    incomeCount: -1,
  });
}

export async function addExpenseToStats(entry) {
  const monthKey = monthKeyFromDate(entry?.date || entry?.createdAt || new Date());
  const amount = Number(entry?.amount || 0);

  await applyStatsDelta(monthKey, {
    expensesTotal: amount,
    expensesCount: 1,
  });
}

export async function updateExpenseInStats(prevEntry, nextEntry) {
  const prevMonthKey = monthKeyFromDate(prevEntry?.date || prevEntry?.createdAt || new Date());
  const nextMonthKey = monthKeyFromDate(nextEntry?.date || nextEntry?.createdAt || new Date());

  if (prevMonthKey !== nextMonthKey) {
    await removeExpenseFromStats(prevEntry);
    await addExpenseToStats(nextEntry);
    return;
  }

  const prevAmount = Number(prevEntry?.amount || 0);
  const nextAmount = Number(nextEntry?.amount || 0);

  await applyStatsDelta(nextMonthKey, {
    expensesTotal: nextAmount - prevAmount,
  });
}

export async function removeExpenseFromStats(entry) {
  const monthKey = monthKeyFromDate(entry?.date || entry?.createdAt || new Date());
  const amount = Number(entry?.amount || 0);

  await applyStatsDelta(monthKey, {
    expensesTotal: -amount,
    expensesCount: -1,
  });
}

export async function addPaymentToStats(entry) {
  const monthKey = monthKeyFromDate(entry?.date || entry?.createdAt || new Date());
  const amount = Number(entry?.amount || 0);

  await applyStatsDelta(monthKey, {
    paymentsTotal: amount,
    paymentsCount: 1,
  });
}

export async function updatePaymentInStats(prevEntry, nextEntry) {
  const prevMonthKey = monthKeyFromDate(prevEntry?.date || prevEntry?.createdAt || new Date());
  const nextMonthKey = monthKeyFromDate(nextEntry?.date || nextEntry?.createdAt || new Date());

  if (prevMonthKey !== nextMonthKey) {
    await removePaymentFromStats(prevEntry);
    await addPaymentToStats(nextEntry);
    return;
  }

  const prevAmount = Number(prevEntry?.amount || 0);
  const nextAmount = Number(nextEntry?.amount || 0);

  await applyStatsDelta(nextMonthKey, {
    paymentsTotal: nextAmount - prevAmount,
  });
}

export async function removePaymentFromStats(entry) {
  const monthKey = monthKeyFromDate(entry?.date || entry?.createdAt || new Date());
  const amount = Number(entry?.amount || 0);

  await applyStatsDelta(monthKey, {
    paymentsTotal: -amount,
    paymentsCount: -1,
  });
}

export { monthKeyFromDate };

export async function updateMonthlyStatsIncome(prevEntry, nextEntry) {
  if (prevEntry && nextEntry) {
    return updateIncomeInStats(prevEntry, nextEntry);
  }
  if (!prevEntry && nextEntry) {
    return addIncomeToStats(nextEntry);
  }
  if (prevEntry && !nextEntry) {
    return removeIncomeFromStats(prevEntry);
  }
}

export async function updateMonthlyStatsExpense(prevEntry, nextEntry) {
  if (prevEntry && nextEntry) {
    return updateExpenseInStats(prevEntry, nextEntry);
  }
  if (!prevEntry && nextEntry) {
    return addExpenseToStats(nextEntry);
  }
  if (prevEntry && !nextEntry) {
    return removeExpenseFromStats(prevEntry);
  }
}

export async function updateMonthlyStatsPayment(prevEntry, nextEntry) {
  if (prevEntry && nextEntry) {
    return updatePaymentInStats(prevEntry, nextEntry);
  }
  if (!prevEntry && nextEntry) {
    return addPaymentToStats(nextEntry);
  }
  if (prevEntry && !nextEntry) {
    return removePaymentFromStats(prevEntry);
  }
}

export async function updateMonthlyStatsInvoice(prevEntry, nextEntry) {
  if (prevEntry && nextEntry) {
    return updateInvoiceInStats(prevEntry, nextEntry);
  }
  if (!prevEntry && nextEntry) {
    return addInvoiceToStats(nextEntry);
  }
  if (prevEntry && !nextEntry) {
    return removeInvoiceFromStats(prevEntry);
  }
}
